<template lang="pug" src="./generateVideoDrawer.pug"></template>

<i18n>
{
    "de": {
        "Generate AI Video": "KI-Video generieren",
        "Resolution": "Auflösung",
        "Select images": "Bilder auswählen",
        "Variants": "Varianten",
        "Choose variant": "Variante wählen",
        "kind.original": "Original",
        "kind.crop": "Zuschnitt",
        "kind.ai-edit": "KI-Bearbeitung",
        "Click images in the order they should play.": "Bilder in der gewünschten Reihenfolge anklicken.",
        "Add image": "Bild hinzufügen",
        "Remove": "Entfernen",
        "Added for this video": "Nur für dieses Video hinzugefügt",
        "Upload or pick from the media library — used for this video only, not added to the product.": "Hochladen oder aus der Mediathek wählen – nur für dieses Video, nicht zum Produkt hinzugefügt.",
        "Suggestions": "Vorschläge",
        "Custom prompt": "Eigener Prompt",
        "Error": "Fehler",
        "Generation failed": "Generierung fehlgeschlagen",
        "More suggestions": "Weitere Vorschläge",
        "360°: the first image is reused as the final frame to complete one continuous rotation.": "360°: Das erste Bild wird als letztes Bild wiederverwendet, um eine durchgehende Rotation zu vervollständigen.",
        "Prompt": "Prompt",
        "Rephrase": "Neu formulieren",
        "Phrasing…": "Wird formuliert…",
        "Review and edit this prompt before generating.": "Prüfe und bearbeite diesen Prompt vor dem Generieren.",
        "Describe the video you want to create…": "Beschreibe das gewünschte Video…",
        "Model": "Modell",
        "Duration per pair": "Dauer pro Übergang",
        "Transition": "Übergang",
        "Estimated cost": "Geschätzte Kosten",
        "Video generation": "Videogenerierung",
        "maximum": "maximal",
        "up to": "bis zu",
        "Billed are the actual generation costs, at most this amount.": "Abgerechnet werden die tatsächlichen Generierungskosten, höchstens dieser Betrag.",
        "AI preparation": "KI-Aufbereitung",
        "this generation": "diese Generierung",
        "Actual cost of the AI suggestions and prompt prepared for this generation.": "Tatsächliche Kosten der KI-Vorschläge und des Prompts für diese Generierung.",
        "Generate": "Generieren",
        "Cancel": "Abbrechen",
        "Loading suggestions": "Vorschläge werden geladen",
        "You can close this drawer. The video will appear in the media library when it is ready.": "Du kannst diesen Bereich schließen. Das Video erscheint in der Medienbibliothek, sobald es fertig ist.",
        "unit.seconds": "Sekunde | Sekunden",
        "unit.transitions": "Übergang | Übergänge",
        "phase.submitting": "Wird übermittelt…",
        "phase.generating": "Wird generiert…",
        "phase.downloading": "Wird heruntergeladen…",
        "phase.concatenating": "Clips werden zusammengefügt…",
        "phase.storing": "Wird gespeichert…",
        "phase.starting": "Wird gestartet…"
    },
    "en": {
        "Duration per pair": "Duration per transition",
        "kind.original": "Original",
        "kind.crop": "Crop",
        "kind.ai-edit": "AI edit",
        "Review and edit this prompt before generating.": "Review and edit this prompt before generating.",
        "unit.seconds": "second | seconds",
        "unit.transitions": "transition | transitions",
        "phase.submitting": "Submitting…",
        "phase.generating": "Generating…",
        "phase.downloading": "Downloading…",
        "phase.concatenating": "Joining clips…",
        "phase.storing": "Saving…",
        "phase.starting": "Starting…"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore } from '@/stores';
import api from '@/extensions/generative-media/store/api/generativeMedia';
import { productImageOptions } from '@/extensions/generative-media/imageThumbnail';
import { moneyFormat, preciseMoneyFormat } from '@/filters';
import Echo from '@/echo';
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog';
import ModelSelectOption from '@/extensions/generative-media/components/ModelSelectOption';

export default {
    name: 'GenerateVideoDrawer',
    components: {
        ImageLibraryDialog,
        ModelSelectOption,
    },
    props: {
        visible: { type: Boolean, default: false },
        // The subject is a product OR an article (articles get the same flow). `product`
        // holds whichever — it just needs `.id` + `.media`. `subjectType` decides whether
        // we send product_id or article_id and which progress channel to use.
        product: { type: Object, required: true },
        subjectType: { type: String, default: 'product' },
    },
    data() {
        return {
            loadingModels: false,
            loadingSuggestions: false,
            loadingMore: false,
            suggestionsRequestId: 0,
            generating: false,
            models: [],
            suggestions: [],
            selectedKeys: [], // ordered tile keys (product 'img:<id>' or ephemeral 'eph:<fileId>')
            ephemeralFrames: [], // uploaded / media-library frames: { fileId, thumbnail, name }
            libraryVisible: false,
            variantChoice: {}, // imageId -> chosen variant File id (default = active layer)
            selectedModelKey: null,
            // Cost lever (Seedance only). Null = CMS default, then the model default.
            selectedResolution: null,
            selectedOptionKey: null,
            selectedOptionDescription: null,
            phrasedPrompt: '',
            phrasing: false,
            loopToStart: false,
            orchestrationCost: 0,
            orchestrationBaseline: 0,
            perPairDurations: [],
            generationProgress: null,
            generationPhase: null,
            statusLog: null,
            channel: null,
            // Ceiling estimate, fetched async once a resolution lever choice is known
            // (720p vs 1080p differ 2.25x on Seedance) — the flat product is shown
            // immediately as a placeholder while it loads.
            fetchedCeiling: null,
            ceilingRequestToken: 0,
        };
    },
    computed: {
        cmsId() {
            const id = _.get(useSetupStore().activeCms, 'id');
            return id ? parseInt(id, 10) : null;
        },
        // The subject's id, and the mutation-variable fragment ({product_id} or
        // {article_id}) so generate / phrase send the right one.
        subjectId() {
            return parseInt(this.product.id, 10);
        },
        subjectVars() {
            return this.subjectType === 'article'
                ? { article_id: this.subjectId }
                : { product_id: this.subjectId };
        },
        // suggestOptions is positional; send the id under the right slot.
        suggestProductId() {
            return this.subjectType === 'article' ? null : this.subjectId;
        },
        suggestArticleId() {
            return this.subjectType === 'article' ? this.subjectId : null;
        },
        availableImages() {
            return productImageOptions(this.product);
        },
        // Every selectable key frame: the product images plus any ephemeral frames the
        // user uploaded / picked from the media library (used for THIS video only — they
        // never join the product gallery). Ephemeral tiles carry no product Image.
        frameTiles() {
            const product = this.availableImages.map(image => ({
                key: 'img:' + image.id,
                ephemeral: false,
                imageId: image.id,
                fileId: image.activeFileId,
                thumbnail: image.thumbnail,
                name: image.name,
                frames: image.frames,
                activeFileId: image.activeFileId,
                hasVariants: image.hasVariants,
            }));
            const ephemeral = this.ephemeralFrames.map(frame => ({
                key: 'eph:' + frame.fileId,
                ephemeral: true,
                imageId: null,
                fileId: frame.fileId,
                thumbnail: frame.thumbnail,
                name: frame.name,
                frames: [],
                activeFileId: frame.fileId,
                hasVariants: false,
            }));
            return [...product, ...ephemeral];
        },
        // Selected PRODUCT image ids only — the Mistral suggestions are about the
        // product, so ephemeral frames don't feed them. Falls back to all product
        // images when only ephemeral frames are selected, so the brain still sees the product.
        suggestionImageIds() {
            const selected = this.selectedKeys
                .filter(key => key.indexOf('img:') === 0)
                .map(key => parseInt(key.slice(4), 10));
            if (selected.length) return selected;
            return this.availableImages.map(image => image.id);
        },
        selectedModel() {
            return this.models.find(m => m.key === this.selectedModelKey) || null;
        },
        resolutionOptions() {
            return _.get(this.selectedModel, 'resolutionOptions') || [];
        },
        durationMin() {
            return _.get(this.selectedModel, 'durationMin') || 3;
        },
        durationMax() {
            return _.get(this.selectedModel, 'durationMax') || 15;
        },
        // Discrete allowed durations (e.g. Kling v2.6: [5, 10]). When set, the model
        // accepts ONLY these exact values — sending anything else makes fal reject the
        // whole submission (422). Null = any integer in [durationMin, durationMax].
        durationSteps() {
            const steps = _.get(this.selectedModel, 'durationSteps');
            return (Array.isArray(steps) && steps.length) ? steps.slice().sort((a, b) => a - b) : null;
        },
        // el-slider step. For a discrete model the step is the gap between allowed
        // values (v2.6 [5,10] → step 5, so the slider can only land on 5 or 10).
        // Continuous models step by 1. (Uniform gaps — true for the current catalog.)
        durationStep() {
            const steps = this.durationSteps;
            return (steps && steps.length >= 2) ? (steps[1] - steps[0]) : 1;
        },
        // For a 360, the first frame is appended as the FINAL key frame so the
        // closing clip has a REAL end frame (e.g. back→front). This is essential:
        // without a provided end frame Kling would invent the unseen view and
        // corrupt product details. The "turn-and-reverse" bug is NOT caused by this
        // — it's Kling taking the shortest path to the end frame; the single-
        // direction prompt fixes that (rotate the long way, never reverse).
        effectiveKeys() {
            if (this.loopToStart && this.selectedKeys.length >= 2) {
                return [...this.selectedKeys, this.selectedKeys[0]];
            }
            return this.selectedKeys;
        },
        // Each resolved key frame as { imageId, fileId } in play order. image_ids carries
        // null for ephemeral frames; the bare File goes through frame_file_ids.
        effectiveFrames() {
            return this.effectiveKeys.map(key => this.resolvedFrame(key)).filter(Boolean);
        },
        effectiveImageIds() {
            return this.effectiveFrames.map(frame => frame.imageId);
        },
        // The chosen File for each key frame, parallel to effectiveImageIds.
        effectiveFrameFileIds() {
            return this.effectiveFrames.map(frame => frame.fileId);
        },
        clipCount() {
            return Math.max(1, this.effectiveFrames.length - 1);
        },
        estimatedSeconds() {
            const durations = this.perPairDurations.slice(0, this.clipCount);
            const total = durations.reduce((acc, n) => acc + (Number(n) || 0), 0);
            if (total > 0) return total;
            return this.clipCount * (this.durationMin + this.durationMax) / 2;
        },
        flatEstimate() {
            const price = _.get(this.selectedModel, 'effectivePrice');
            if (!price) return null;
            return (price * this.estimatedSeconds).toFixed(2);
        },
        estimatedCost() {
            return this.fetchedCeiling !== null ? this.fetchedCeiling : this.flatEstimate;
        },
        canGenerate() {
            return !this.generating
                && !this.phrasing
                && this.selectedKeys.length > 0
                && this.selectedModelKey
                && this.selectedOptionKey !== null
                && this.phrasedPrompt.trim().length > 0;
        },
        secondsUnit() {
            return this.$tc('unit.seconds', this.estimatedSeconds);
        },
        estimatedCostFormatted() {
            if (!this.estimatedCost) return null;
            return moneyFormat(this.estimatedCost, 'EUR');
        },
        orchestrationCostFormatted() {
            // Actual (proportional) cost — small, so show extra precision rather
            // than rounding sub-cent amounts to 0,00 €.
            const language = window.navigator.userLanguage || window.navigator.language;
            return Intl.NumberFormat(language, {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
            }).format(this.orchestrationCost || 0);
        },
        phaseLabel() {
            const key = this.generationPhase || 'starting';
            return this.$t('phase.' + key);
        },
    },
    watch: {
        visible(value) {
            if (value) this.onOpen();
            else this.onClose();
        },
        // Switching models can change the allowed durations (v3 continuous 3–15 →
        // v2.6 discrete 5/10). Re-snap every clip duration to a value the new model
        // accepts so the UI never carries an invalid value (e.g. a leftover 7s) into
        // a submission fal would reject.
        selectedModelKey() {
            this.perPairDurations = this.perPairDurations.map(seconds => this.clampDuration(seconds));
            // The resolution chips hide when the new model offers no options, so a
            // leftover value would keep being submitted while nothing shows it.
            if (!this.resolutionOptions.includes(this.selectedResolution)) {
                this.selectedResolution = null;
            }
            this.debouncedFetchCeiling();
        },
        selectedKeys(newKeys) {
            // Any previously chosen option/prompt is stale once the selection changes.
            this.selectedOptionKey = null;
            this.selectedOptionDescription = null;
            this.phrasedPrompt = '';
            this.loopToStart = false;
            this.resizeDurations();

            if (newKeys.length > 0) {
                // Invalidate any in-flight suggestion request and show the spinner
                // immediately, so a slower earlier response can't flicker in before
                // the final one. Suggestions depend on the whole selection, so this
                // re-runs (debounced) on every change.
                this.suggestionsRequestId++;
                this.suggestions = [];
                this.loadingSuggestions = true;
                this.debouncedLoadSuggestions();
            } else {
                this.suggestionsRequestId++;
                this.suggestions = [];
                this.loadingSuggestions = false;
            }
        },
        // Function-form, not string-handler-name form: a string handler resolves
        // via vm[handler] once during initWatch, which runs BEFORE created() — so
        // debouncedFetchCeiling (assigned in created(), below) would be captured
        // as undefined and crash on first fire. A function watcher only reads it
        // when it actually fires.
        selectedResolution() { this.debouncedFetchCeiling(); },
        estimatedSeconds() { this.debouncedFetchCeiling(); },
        cmsId() { this.debouncedFetchCeiling(); },
    },
    created() {
        this.debouncedLoadSuggestions = _.debounce(this.loadSuggestions, 400);
        this.debouncedFetchCeiling = _.debounce(this.fetchCeiling, 300);
    },
    beforeDestroy() {
        // Pending debounces would otherwise fire their requests after the drawer closes.
        this.debouncedLoadSuggestions.cancel();
        this.debouncedFetchCeiling.cancel();
    },
    methods: {
        async fetchCeiling() {
            const token = ++this.ceilingRequestToken;

            if (!this.cmsId || !this.selectedModelKey || !this.estimatedSeconds) {
                this.fetchedCeiling = null;
                return;
            }

            try {
                // estimatedSeconds can be a non-integer average (durationMin/Max midpoint)
                // before any clip duration is set — the GraphQL arg is Int.
                const ceiling = await api.costCeiling(this.cmsId, this.selectedModelKey, Math.round(this.estimatedSeconds), {
                    resolution: this.selectedResolution,
                });

                if (token === this.ceilingRequestToken) {
                    this.fetchedCeiling = ceiling;
                }
            } catch (error) {
                if (token === this.ceilingRequestToken) {
                    this.fetchedCeiling = null;
                }
            }
        },
        tileByKey(key) {
            return this.frameTiles.find(tile => tile.key === key) || null;
        },
        // Resolve a selected tile key into the key frame the backend expects:
        // a product image (+ its chosen variant File) or a bare ephemeral File.
        resolvedFrame(key) {
            const tile = this.tileByKey(key);
            if (!tile) return null;
            if (tile.ephemeral) {
                return { imageId: null, fileId: tile.fileId };
            }
            return { imageId: tile.imageId, fileId: this.frameFileIdFor(tile.imageId) };
        },
        toggle(key) {
            if (this.selectedKeys.includes(key)) {
                this.selectedKeys = this.selectedKeys.filter(selected => selected !== key);
            } else {
                this.selectedKeys = [...this.selectedKeys, key];
            }
        },
        orderOf(key) {
            return this.selectedKeys.indexOf(key) + 1;
        },
        // --- per-image variant (layer) choice ---
        imageOption(imageId) {
            return this.availableImages.find(image => image.id === imageId) || null;
        },
        // The File id used for an image's key frame: an explicit choice, else its
        // active layer.
        frameFileIdFor(imageId) {
            if (this.variantChoice[imageId]) {
                return this.variantChoice[imageId];
            }
            const option = this.imageOption(imageId);
            return option ? option.activeFileId : null;
        },
        chosenFileId(tile) {
            return this.variantChoice[tile.imageId] || tile.activeFileId;
        },
        // The thumbnail shown for a tile = its chosen layer's thumbnail.
        frameThumb(tile) {
            const fileId = this.chosenFileId(tile);
            const frame = (tile.frames || []).find(f => f.fileId === fileId);
            return (frame && frame.thumbnail) || tile.thumbnail;
        },
        frameKindLabel(frame) {
            return this.$t('kind.' + (frame.kind || 'crop'));
        },
        // Pick a specific layer for a product tile; selects it into the sequence
        // if it isn't already.
        chooseVariant(tile, frame) {
            this.$set(this.variantChoice, tile.imageId, frame.fileId);
            if (!this.selectedKeys.includes(tile.key)) {
                this.toggle(tile.key);
            }
        },
        // --- ephemeral frames (upload / media library), used for this video only ---
        openLibrary() {
            this.libraryVisible = true;
        },
        onLibrarySelect(selection) {
            const selected = Array.isArray(selection) ? selection : [selection];
            selected.forEach(image => {
                const fileId = parseInt(_.get(image, 'source.id'), 10);
                if (!fileId || this.ephemeralFrames.some(frame => frame.fileId === fileId)) return;
                this.ephemeralFrames.push({
                    fileId,
                    thumbnail: _.get(image, 'url') || _.get(image, 'source.url') || null,
                    name: _.get(image, 'name') || _.get(image, 'source.name') || '',
                });
                // Auto-select the newly added frame into the play sequence.
                this.toggle('eph:' + fileId);
            });
        },
        removeEphemeral(tile) {
            this.selectedKeys = this.selectedKeys.filter(key => key !== tile.key);
            this.ephemeralFrames = this.ephemeralFrames.filter(frame => frame.fileId !== tile.fileId);
        },
        // Snap a requested duration to a value the model accepts (mirrors the backend
        // clamp), so the UI never offers a value fal would reject with a 422.
        clampDuration(seconds) {
            const value = Number(seconds);
            const steps = this.durationSteps;
            if (steps && steps.length) {
                return steps.reduce((best, step) => Math.abs(step - value) < Math.abs(best - value) ? step : best, steps[0]);
            }
            return Math.max(this.durationMin, Math.min(this.durationMax, value || this.durationMin));
        },
        // Keep one duration slider per transition (clipCount), preserving values and
        // snapping each to a model-valid duration (discrete models accept only their
        // exact steps; continuous models clamp into range).
        resizeDurations() {
            const fallback = this.durationSteps ? this.durationSteps[0] : Math.floor((this.durationMin + this.durationMax) / 2);
            this.perPairDurations = this.selectedKeys.length === 0
                ? []
                : Array.from({ length: this.clipCount }).map((unused, index) => this.clampDuration(this.perPairDurations[index] || fallback));
        },
        selectOption(suggestion) {
            this.selectedOptionKey = suggestion.key;
            this.selectedOptionDescription = suggestion.description;
            // A "360° turntable" suggestion loops: append the first frame to close
            // it. This drives the effective sequence, the extra transition + cost,
            // and the clockwise-360 phrasing — no separate toggle needed.
            this.loopToStart = suggestion.key !== 'custom' && !!suggestion.loop;
            this.resizeDurations();
            if (suggestion.key === 'custom') {
                // Custom: the user writes the final prompt themselves.
                this.phrasedPrompt = '';
                return;
            }
            this.phraseSelected();
        },
        async phraseSelected() {
            this.phrasing = true;
            try {
                this.phrasedPrompt = await api.phrasePrompt({
                    ...this.subjectVars,
                    capability: 'video',
                    option_key: this.selectedOptionKey,
                    option_description: this.selectedOptionDescription,
                    loop: this.loopToStart,
                });
            } catch (error) {
                // Fall back to the suggestion text so the user still has something editable.
                this.phrasedPrompt = this.selectedOptionDescription || '';
            } finally {
                this.phrasing = false;
                this.resizePrompt();
                this.refreshOrchestrationCost();
            }
        },
        // el-input autosize doesn't recompute on programmatic v-model changes, so
        // trigger it manually after the prompt is populated (avoids a scrollbar).
        resizePrompt() {
            this.$nextTick(() => {
                const input = this.$refs.promptInput;
                if (input && typeof input.resizeTextarea === 'function') {
                    input.resizeTextarea();
                }
            });
        },
        rephrase() {
            if (this.selectedOptionKey && this.selectedOptionKey !== 'custom') {
                this.phraseSelected();
            }
        },
        // Actual AI-prep cost for THIS generation only: the period orchestration
        // cost rises proportionally with each suggest/phrase, so we snapshot a
        // baseline when the drawer opens and show only the delta consumed here.
        async refreshOrchestrationCost() {
            if (!this.cmsId) return;
            try {
                const current = await api.incurredCost(this.cmsId, 'video') || 0;
                this.orchestrationCost = Math.max(0, current - this.orchestrationBaseline);
            } catch (error) {
                // Keep the previous value on failure.
            }
        },
        // The COLLAPSED select value only — the expanded list renders
        // <model-select-option>, which owns the two-line layout.
        modelLabel(model) {
            const price = preciseMoneyFormat(model.effectivePrice);
            const unit = this.$tc('unit.' + model.unit, 1);
            return `${model.label} · ${this.$t('up to')} ${price}/${unit}`;
        },
        async onOpen() {
            this.resetState();
            this.loadingModels = true;
            try {
                this.models = await api.fetchModels('video', this.cmsId);
                const settings = await api.fetchSettings(this.cmsId);
                this.selectedModelKey = _.get(settings, 'default_video_model_key') || _.get(this.models, '[0].key', null);
            } finally {
                this.loadingModels = false;
            }
            // Snapshot the period orchestration cost so this session's delta starts at 0.
            try {
                this.orchestrationBaseline = await api.incurredCost(this.cmsId, 'video') || 0;
            } catch (error) {
                this.orchestrationBaseline = 0;
            }
            this.orchestrationCost = 0;
        },
        onClose() {
            this.unsubscribe();
        },
        async loadSuggestions() {
            if (this.selectedKeys.length === 0) return;

            // Tie this request to the current selection; if the selection changes
            // again before it resolves, its result is discarded (no flicker).
            const requestId = this.suggestionsRequestId;
            this.loadingSuggestions = true;
            try {
                const result = await api.suggestOptions(this.suggestProductId, 'video', this.suggestionImageIds, this.$i18n.locale, undefined, undefined, undefined, undefined, this.suggestArticleId);
                if (requestId !== this.suggestionsRequestId) return;
                this.suggestions = result;
            } catch (error) {
                if (requestId !== this.suggestionsRequestId) return;
                this.suggestions = [{ key: 'custom', label: this.$t('Custom prompt'), description: '' }];
            } finally {
                if (requestId === this.suggestionsRequestId) this.loadingSuggestions = false;
                this.refreshOrchestrationCost();
            }
        },
        // Fetch additional, distinct suggestions and append them (custom stays last).
        async loadMoreSuggestions() {
            if (this.selectedKeys.length === 0 || this.loadingMore) return;

            const requestId = this.suggestionsRequestId;
            const existingLabels = this.suggestions
                .filter(suggestion => suggestion.key !== 'custom')
                .map(suggestion => suggestion.label);

            this.loadingMore = true;
            try {
                const more = await api.suggestOptions(
                    this.suggestProductId,
                    'video',
                    this.suggestionImageIds,
                    this.$i18n.locale,
                    existingLabels,
                    undefined,
                    undefined,
                    undefined,
                    this.suggestArticleId,
                );
                // Selection changed while loading more — discard this result.
                if (requestId !== this.suggestionsRequestId) return;
                const normalize = suggestion => (suggestion.label || '').trim().toLowerCase();
                const seenKeys = new Set(this.suggestions.map(suggestion => suggestion.key));
                const seenLabels = new Set(this.suggestions.map(normalize));
                const hasLoop = this.suggestions.some(suggestion => suggestion.loop);
                const fresh = more.filter(suggestion =>
                    suggestion.key !== 'custom'
                    && !seenKeys.has(suggestion.key)
                    && !seenLabels.has(normalize(suggestion))
                    && !(suggestion.loop && hasLoop) // only ever one 360° option
                );
                const custom = this.suggestions.filter(suggestion => suggestion.key === 'custom');
                const others = this.suggestions.filter(suggestion => suggestion.key !== 'custom');
                this.suggestions = [...others, ...fresh, ...custom];
            } catch (error) {
                // Keep the existing suggestions on failure.
            } finally {
                this.loadingMore = false;
                this.refreshOrchestrationCost();
            }
        },
        async generate() {
            if (!this.canGenerate) return;
            this.generating = true;
            try {
                this.statusLog = await api.generateVideo({
                    ...this.subjectVars,
                    image_ids: this.effectiveImageIds,
                    frame_file_ids: this.effectiveFrameFileIds,
                    model_key: this.selectedModelKey,
                    per_pair_durations: this.perPairDurations,
                    option_key: this.selectedOptionKey,
                    option_description: this.selectedOptionDescription,
                    final_prompt: this.phrasedPrompt,
                    loop: this.loopToStart,
                    resolution: this.selectedResolution,
                });
                this.subscribeToProgress();
                // Tell the product form a generation is now running so it can show a
                // placeholder tile in the media grid — visible even if this drawer is closed.
                this.$emit('generation-started', this.statusLog);
            } catch (error) {
                this.generating = false;
                this.notifyError('Error', String(_.get(error, 'message') || error));
            }
        },
        // System-wide notification convention: bottom-left, dark.
        notifyError(title, message) {
            this.$notify.error({
                title: this.$t(title),
                message,
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
        subscribeToProgress() {
            if (!Echo.client) Echo.initialize();
            // Subscribe ONCE for the drawer's lifetime. Re-listening on the cached Echo
            // channel would stack duplicate handlers; leaving it (old behaviour) tore down
            // the SAME channel the product form (Main.vue) owns, so a video generated with
            // the drawer closed never refreshed into the gallery.
            if (this.channel) return;
            this.channel = Echo.client.private(`generative-media.${this.subjectType}.${this.product.id}`);
            this.channel.listen('.task:progress', (payload) => {
                this.generationProgress = payload.progress;
                this.generationPhase = payload.phase;
            });
            this.channel.listen('.task:completed', (payload) => {
                this.generationProgress = 100;
                this.generating = false;
                this.$emit('completed', payload);
                this.$emit('update:visible', false);
            });
            this.channel.listen('.task:failed', (payload) => {
                this.generating = false;
                this.notifyError('Generation failed', payload.error);
            });
        },
        unsubscribe() {
            // Intentionally does NOT Echo.leave() the product channel — Main.vue owns that
            // channel's lifecycle (it leaves on its own teardown). Leaving it here would
            // kill Main's completion listener and the finished video would never appear.
            // The drawer's listeners are harmless once it's closed (idempotent updates).
        },
        resetState() {
            this.fetchedCeiling = null;
            this.selectedResolution = null;
            this.suggestions = [];
            this.selectedKeys = [];
            this.ephemeralFrames = [];
            this.libraryVisible = false;
            this.variantChoice = {};
            this.selectedOptionKey = null;
            this.selectedOptionDescription = null;
            this.phrasedPrompt = '';
            this.phrasing = false;
            this.loopToStart = false;
            this.orchestrationCost = 0;
            this.orchestrationBaseline = 0;
            this.perPairDurations = [];
            this.generationProgress = null;
            this.generationPhase = null;
            this.statusLog = null;
            this.generating = false;
        },
    },
};
</script>

<style lang="stylus" scoped>
// Static shop brand primary ($--shop-color-primary in _variables.sass)
$brand = #6E26FF

.gm-form
    padding-top 20px
    padding-bottom 20px

.gm-section
    & + &
        margin-top 26px

.gm-label
    display block
    font-size 13px
    font-weight 600
    color #303133
    margin-bottom 10px

.gm-hint
    font-size 12px
    line-height 1.4
    color #909399
    margin-top -4px
    margin-bottom 10px

    &--below
        margin-top 6px
        margin-bottom 0

    // 360° keyframe guidance — brand-accented, icon-led, so it reads as a helpful tip.
    &--360
        display flex
        align-items flex-start
        gap 6px
        color $brand
        margin-top 2px
        i
            margin-top 2px
            flex 0 0 auto

.gm-ratios
    display flex
    flex-wrap wrap
    gap 6px

.gm-ratio
    border 1px solid #dcdfe6
    background #fff
    color #606266
    border-radius 6px
    padding 5px 10px
    font-size 12px
    line-height 1
    cursor pointer
    transition all .15s ease

    &:hover
        border-color $brand
        color $brand

    &.active
        background $brand
        border-color $brand
        color #fff

// Image picker
.gm-grid
    display flex
    flex-wrap wrap
    gap 10px

// Layer-picker toggle on a tile (only when the image has layers).
.gm-layers-toggle
    position absolute
    bottom 4px
    right 4px
    width 22px
    height 22px
    border-radius 11px
    border none
    background rgba(110, 38, 255, .9)
    color #fff
    font-size 11px
    cursor pointer
    display flex
    align-items center
    justify-content center
    box-shadow 0 1px 3px rgba(0, 0, 0, .35)

    &:hover
        background $brand

// Layer picker popover content
.gm-layer-pop
    &__title
        font-size 11px
        font-weight 600
        color #303133
        margin-bottom 8px

    &__row
        display flex
        flex-wrap wrap
        gap 6px

.gm-variant
    position relative
    width 32px
    height 32px
    border-radius 6px
    overflow hidden
    cursor pointer
    border 2px solid #e4e7ed
    padding 0
    background none

    img
        width 100%
        height 100%
        object-fit cover
        display block

    &:hover
        border-color lighten($brand, 30%)

    &.active
        border-color $brand

    &__kind
        position absolute
        bottom 1px
        right 1px
        font-size 8px
        color #fff
        background rgba(0, 0, 0, .5)
        border-radius 3px
        padding 1px 2px
        line-height 1

.gm-tile
    position relative
    width 76px
    height 76px
    border-radius 8px
    overflow hidden
    cursor pointer
    border 2px solid #e4e7ed
    transition border-color .15s ease, transform .15s ease

    img
        width 100%
        height 100%
        object-fit cover
        display block

    &:hover
        border-color lighten($brand, 30%)
        transform translateY(-1px)

    &.selected
        border-color $brand

    &__order
        position absolute
        top 4px
        left 4px
        min-width 20px
        height 20px
        padding 0 5px
        border-radius 10px
        background $brand
        color #fff
        font-size 12px
        font-weight 600
        line-height 20px
        text-align center
        box-shadow 0 1px 3px rgba(0, 0, 0, .3)

    // Ephemeral (upload / library) frame: corner badge + discard button.
    &__badge
        position absolute
        bottom 4px
        left 4px
        width 18px
        height 18px
        border-radius 9px
        background rgba(0, 0, 0, .55)
        color #fff
        font-size 9px
        display flex
        align-items center
        justify-content center

    &__remove
        position absolute
        top 4px
        right 4px
        width 18px
        height 18px
        border-radius 9px
        border none
        padding 0
        background rgba(0, 0, 0, .55)
        color #fff
        font-size 9px
        cursor pointer
        display flex
        align-items center
        justify-content center

        &:hover
            background #f56c6c

// Add-frame tile (opens the upload / media-library picker)
.gm-tile--add
    display flex
    align-items center
    justify-content center
    border 2px dashed #dcdfe6
    background #fafafa
    color $brand
    font-size 20px

    &:hover
        border-color $brand
        background lighten($brand, 45%)

// "More suggestions" chip
.gm-chip.gm-more
    border-style dashed
    color $brand

    &:disabled
        color #c0c4cc
        border-color #dcdfe6
        cursor not-allowed

// 360° loop note in the duration section
.gm-loop-note
    display flex
    align-items center
    gap 6px
    margin-bottom 12px
    font-size 12px
    color $brand

// Suggestion chips
.gm-chips
    display flex
    flex-wrap wrap
    gap 8px

// Shimmering skeleton chips while suggestions load
.gm-chip-skeleton
    display inline-block
    width 130px // base width (used for the "load more" skeletons appended after chips)
    height 31px
    border-radius 18px
    background linear-gradient(90deg, #f0f0f2 25%, #e6e6ea 37%, #f0f0f2 63%)
    background-size 400% 100%
    animation gm-shimmer 1.4s ease infinite

    &:nth-child(1)
        width 150px
    &:nth-child(2)
        width 110px
    &:nth-child(3)
        width 175px
    &:nth-child(4)
        width 125px
    &:nth-child(5)
        width 95px

@keyframes gm-shimmer
    0%
        background-position 100% 50%
    100%
        background-position 0 50%

.gm-chip
    display inline-flex
    align-items center
    gap 6px
    padding 7px 14px
    border-radius 18px
    border 1px solid #dcdfe6
    background #fff
    color #606266
    font-size 13px
    line-height 1
    cursor pointer
    transition all .15s ease

    &:hover
        border-color $brand
        color $brand

    &.active
        background $brand
        border-color $brand
        color #fff

    &.is-custom
        border-style dashed

        &.active
            border-style solid

// Prompt review
.gm-prompt-head
    display flex
    align-items center
    justify-content space-between

.gm-rephrase
    display inline-flex
    align-items center
    gap 5px
    border none
    background none
    padding 0
    cursor pointer
    font-size 12px
    color $brand

    &:hover
        text-decoration underline

    &:disabled
        color #c0c4cc
        cursor not-allowed
        text-decoration none

// Duration sliders
.gm-duration
    & + &
        margin-top 8px

    &__name
        display block
        font-size 12px
        color #909399
        margin-bottom 2px

// Cost summary (pinned above footer)
.gm-cost
    padding 12px 20px
    background #f7f4ff
    border-top 1px solid #ece6ff

    &__row
        display flex
        align-items baseline
        justify-content space-between

        &--muted
            margin-top 6px

    &__line
        display flex
        align-items center
        gap 6px

        .el-icon-info
            color #b8a9e8
            cursor help

    &__label
        font-size 13px
        color #303133

    &__est
        font-size 11px
        color #909399

    &__amount
        font-size 20px
        font-weight 700
        color $brand

    &__amount--muted
        font-size 14px
        font-weight 600
        color #8a8a99

    &__meta
        font-size 12px
        color #909399
        margin-top 2px

// Generating state
.gm-generating
    display flex
    flex-direction column
    align-items center
    justify-content center
    text-align center
    padding 48px 32px
    height 100%

    &__phase
        margin-top 20px
        font-size 15px
        font-weight 600
        color #303133

    &__hint
        margin-top 10px
        max-width 320px
        font-size 12px
        line-height 1.5
        color #909399
</style>
